import { createSelector } from 'reselect';

import { LoadingState } from '../../domain/schemas';
import { sortUnitDataListTransition } from '../../domain/unitDataList/sortUnitDataListTransition';
import {
  DataUnitDataListTransitionResponse,
  DataUnitDataListTransitionSearchParams,
} from '../../domain/unitDataList/types';

import { dateTypes } from '../../components/organisms/UnitDataListTransitionTable/dateTypes';

import { addBackgroundColor } from '../../utils/addBackgroundColor';
import { parseNumber } from '../../utils/parseNumber';
import {
  UnitDataListTransitionReportsSettingIsHeatmapEnabledSelector,
  unitDataListTransitionDateRangeSelector,
  unitDataListTransitionOrderSelector,
  unitDataListTransitionSortSelector,
} from '../ui/unitDataListReportsSetting';

/* ---------------------------------------------------------------
 * Action Types
 */

const FETCH_DATA_UNIT_DATA_LIST_TRANSITION =
  'FETCH_DATA_UNIT_DATA_LIST_TRANSITION' as const;
const FETCH_DATA_UNIT_DATA_LIST_TRANSITION_REQUEST =
  'FETCH_DATA_UNIT_DATA_LIST_TRANSITION_REQUEST' as const;
const FETCH_DATA_UNIT_DATA_LIST_TRANSITION_SUCCESS =
  'FETCH_DATA_UNIT_DATA_LIST_TRANSITION_SUCCESS' as const;

const HIDE_DATA_UNIT_DATA_LIST_TRANSITION =
  'HIDE_DATA_UNIT_DATA_LIST_TRANSITION' as const;
const RENEW_DATA_UNIT_DATA_LIST_TRANSITION =
  'RENEW_DATA_UNIT_DATA_LIST_TRANSITION' as const;

export const DataUnitDataListTransitionActionTypes = {
  FETCH_DATA_UNIT_DATA_LIST_TRANSITION,
  FETCH_DATA_UNIT_DATA_LIST_TRANSITION_REQUEST,
  FETCH_DATA_UNIT_DATA_LIST_TRANSITION_SUCCESS,
  HIDE_DATA_UNIT_DATA_LIST_TRANSITION,
  RENEW_DATA_UNIT_DATA_LIST_TRANSITION,
};

/* ---------------------------------------------------------------
 * Action Creators
 */

/**
 * dataUnitDataListTransitionのデータを取得する
 * @param params 検索条件
 */
export const fetchDataUnitDataListTransitionAction = (
  params: DataUnitDataListTransitionSearchParams
) => ({
  type: FETCH_DATA_UNIT_DATA_LIST_TRANSITION,
  payload: { params },
});

/**
 * データ取得開始時に実行
 */
function fetchDataUnitDataListTransitionRequestAction() {
  return {
    type: FETCH_DATA_UNIT_DATA_LIST_TRANSITION_REQUEST,
  };
}

/**
 * データ取得成功時に実行
 * @param dataUnitDataListTransition 取得した推移データ
 */
function fetchDataUnitDataListTransitionSuccessAction(
  dataUnitDataListTransition: DataUnitDataListTransitionResponse
) {
  return {
    type: FETCH_DATA_UNIT_DATA_LIST_TRANSITION_SUCCESS,
    payload: { dataUnitDataListTransition },
  };
}

/** 推移データの情報を初期化 */
function renewDataUnitDataListTransitionAction() {
  return {
    type: RENEW_DATA_UNIT_DATA_LIST_TRANSITION,
  };
}

/**
 * [台別データ推移]テーブルを閉じる
 */
function hideDataUnitDataListTransition() {
  return {
    type: HIDE_DATA_UNIT_DATA_LIST_TRANSITION,
  };
}

export const DataUnitDataListTransitionActionCreators = {
  fetchDataUnitDataListTransitionAction,
  fetchDataUnitDataListTransitionRequestAction,
  fetchDataUnitDataListTransitionSuccessAction,
  renewDataUnitDataListTransitionAction,
  hideDataUnitDataListTransition,
};

/* ---------------------------------------------------------------
 * Actions
 */

export type FetchDataUnitDataListTransitionAction = ReturnType<
  typeof fetchDataUnitDataListTransitionAction
>;

export type FetchDataUnitDataListTransitionRequestAction = ReturnType<
  typeof fetchDataUnitDataListTransitionRequestAction
>;

export type FetchDataUnitDataListTransitionSuccessAction = ReturnType<
  typeof fetchDataUnitDataListTransitionSuccessAction
>;

export type RenewDataUnitDataListTransitionAction = ReturnType<
  typeof renewDataUnitDataListTransitionAction
>;

export type HideDataUnitDataListTransitionAction = ReturnType<
  typeof hideDataUnitDataListTransition
>;

type DataUnitDataListTransitionAction =
  | FetchDataUnitDataListTransitionAction
  | FetchDataUnitDataListTransitionRequestAction
  | FetchDataUnitDataListTransitionSuccessAction
  | RenewDataUnitDataListTransitionAction
  | HideDataUnitDataListTransitionAction;

/* ---------------------------------------------------------------
 * State
 */

type DataUnitDataListTransitionState = {
  dataUnitDataListTransition: DataUnitDataListTransitionResponse | undefined;
  loadingState: LoadingState;
};

export const initialState: DataUnitDataListTransitionState = {
  dataUnitDataListTransition: undefined,
  loadingState: 'prepare',
};

// Selector

/**
 * [台別データ推移]データすべてを取得する
 * @returns 推移テーブルのデータ（未取得の場合undefined）
 */
export function dataUnitDataListTransitionSelector(rootState: {
  dataUnitDataListTransition: DataUnitDataListTransitionState;
}) {
  return rootState.dataUnitDataListTransition;
}

/**
 * [台別データ推移]データのローディング状態を取得する
 * @returns ローディング状態（ローディング中の場合true）
 */
export const dataUnitDataListTransitionLoadingStateSelector = createSelector(
  dataUnitDataListTransitionSelector,
  ({ loadingState }) => loadingState
);

/**
 * [台別データ推移] 取得データからdataを取得する
 *
 */
export const dataUnitDataListTransitionDataSelector = createSelector(
  dataUnitDataListTransitionSelector,
  ({ dataUnitDataListTransition }) => dataUnitDataListTransition
);

/**
 * [台別データ推移] sortされたデータを取得する
 * 最終的な表示にはdataColumnsSwappedSelectorを使用してください
 *
 */
export const dataUnitDataListTransitionSortedDataSelector = createSelector(
  [
    dataUnitDataListTransitionDataSelector,
    unitDataListTransitionSortSelector,
    unitDataListTransitionOrderSelector,
  ],
  (
    dataUnitDataListTransition,
    unitDataListTransitionSort,
    unitDataListTransitionOrder
  ) => {
    if (
      !dataUnitDataListTransition ||
      !unitDataListTransitionSort ||
      !unitDataListTransitionOrder
    ) {
      return dataUnitDataListTransition;
    }

    const sortedDataUnitDataListTransition = sortUnitDataListTransition(
      dataUnitDataListTransition,
      unitDataListTransitionOrder,
      unitDataListTransitionSort
    );

    return sortedDataUnitDataListTransition;
  }
);

/**
 * [台別データ一覧] 推移データテーブルにヒートマップを追加する関数
 *
 */
export const addHeatmapBackgroundColorToUnitDataListTransition = (
  columns: DataUnitDataListTransitionResponse['data']['columns'],
  rows: DataUnitDataListTransitionResponse['data']['rows'],
  dateRange: string
) => {
  // rowsからdataのみを抽出
  const data = rows.map((x) => x.data);

  // ヒートマップ表示対象項目
  const columnsListForHeatMap = [
    'out',
    'outGk',
    'daiYugiJikan',
    'dtmart',
    'spDtmart',
    'tmaArari',
    'spTmaArari',
    'yukoS',
    'nyushoS',
    'x1000YenStart',
    'byCnt',
    'bymin',
    'soGameSu',
    'spHsiTmaArari',
  ];

  // dateRangeの値によってTOPとBOTTOMの表示範囲を決定
  const selectedDateRange = dateTypes.find((date) => date.id === dateRange);
  const rankRange = selectedDateRange?.rankRange ?? 3;

  // 各トグルに応じた、ヒートマップの表示に必要な最小限のデータが揃っているかを確認(平均合計行は省いて計算)
  if (rows.length < rankRange * 2 + 1) {
    return rows;
  }

  // 対象カラムのインデックスを取得
  const heatmapColumnIndices = columns
    .map((col, idx) =>
      columnsListForHeatMap.includes(col.code ?? '') ? idx : -1
    )
    .filter((idx) => idx !== -1);

  const borderNumbers =
    heatmapColumnIndices.map((colIndex) => {
      const values = data
        .slice(1) // 平均合計行を除外
        .map((row) => row?.at(colIndex)?.value)
        .filter(
          (value): value is string => value !== undefined && value !== '-'
        )
        .map((value) => parseNumber(value))
        .sort((a, b) => a - b);

      const top = values[values.length - rankRange];
      const bottom = values[rankRange - 1];

      return [top, bottom];
    }) || [];

  return rows.map((x, index) => ({
    ...x,
    data:
      x.day.at(index) === '-'
        ? x.data
        : addBackgroundColor(borderNumbers, heatmapColumnIndices)(x.data),
  }));
};

/**
 * [台別データ推移] データのヒートマップが実装されたデータを取得する
 * 最終的な表示にはdataColumnsSwappedSelectorを使用してください
 *
 */
export const dataUnitDataListTransitionHeatmappedSelector = createSelector(
  [
    dataUnitDataListTransitionSortedDataSelector,
    UnitDataListTransitionReportsSettingIsHeatmapEnabledSelector,
    unitDataListTransitionDateRangeSelector,
  ],
  (dataUnitDataListTransition, isHeatmapEnabled, dateRange) => {
    // ヒートマップが無効ならそのまま返す
    if (!dataUnitDataListTransition || !isHeatmapEnabled) {
      return dataUnitDataListTransition;
    }

    // tableData を取得
    const tableData = dataUnitDataListTransition?.data;

    // データがない場合、デフォルトの空データを返す
    if (tableData === undefined) {
      return {
        setting: {
          halls: [''],
          fields: [''],
          startDate: '',
          endDate: '',
          excludeToday: false,
          containsAverage: false,
          containsData: false,
          daiBan: [],
          kiList: [''],
        },
        data: {
          columns: [],
          rows: [],
        },
      };
    }

    // データが空の場合もそのまま返す
    if (tableData.rows?.length === 0) {
      return {
        setting: dataUnitDataListTransition.setting, // setting を保持
        data: {
          columns: tableData.columns,
          rows: [],
        },
      };
    }

    // ヒートマップを追加する
    const heatmappedRows = isHeatmapEnabled
      ? addHeatmapBackgroundColorToUnitDataListTransition(
          tableData.columns,
          tableData.rows,
          dateRange
        )
      : tableData.rows;

    // 必要なデータにヒートマップを追加して返す
    return {
      ...dataUnitDataListTransition, // 元のデータを保持
      data: {
        columns: tableData.columns,
        rows: heatmappedRows,
      },
    };
  }
);

/**
 * [台別データ推移] 取得データからdata.settingを取得する
 */
export const dataUnitDataListTransitionSettingSelector = createSelector(
  dataUnitDataListTransitionDataSelector,
  (data) => data?.setting
);

/**
 * [台別データ推移] データの存在確認
 */
export const dataUnitDataListTransitionIsExistSelector = createSelector(
  dataUnitDataListTransitionDataSelector,
  (data) => data?.data !== undefined
);

/* ---------------------------------------------------------------
 * Reducer
 */

export function dataUnitDataListTransitionReducer(
  state = initialState,
  action: DataUnitDataListTransitionAction
): DataUnitDataListTransitionState {
  switch (action.type) {
    case FETCH_DATA_UNIT_DATA_LIST_TRANSITION_REQUEST:
      return {
        ...state,
        loadingState: 'loading',
      };
    case FETCH_DATA_UNIT_DATA_LIST_TRANSITION_SUCCESS:
      return {
        loadingState: 'loaded',
        dataUnitDataListTransition: action.payload.dataUnitDataListTransition,
      };
    case RENEW_DATA_UNIT_DATA_LIST_TRANSITION:
      return initialState;
    case HIDE_DATA_UNIT_DATA_LIST_TRANSITION:
      return {
        ...state,
        dataUnitDataListTransition: initialState.dataUnitDataListTransition,
      };
    default:
      return state;
  }
}
